/**
 * SIS Student Profile — 5 tabs: Personal · Health · Attendance · Grades · Programs
 * Editable for staff with appropriate roles. Includes passport photo upload + transcript PDF.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { User, Heart, CalendarCheck, GraduationCap, Award, ArrowLeft, Save, Camera, FileText, Plus, Trash2, Download } from 'lucide-react';
import { toast } from 'sonner';
import { compressImageToBase64 } from '../lib/imageCompress';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SISStudentProfilePage = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/sis/students/${studentId}/profile`);
      setProfile(res.data);
      setForm(res.data.student || {});
    } catch (e) {
      toast.error('Failed to load profile');
    } finally { setLoading(false); }
  }, [studentId]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const save = async () => {
    try {
      await axios.put(`${API_URL}/api/sis/students/${studentId}`, form);
      toast.success('Saved');
      setEditing(false);
      fetchProfile();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const onPhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const b64 = await compressImageToBase64(file);
      setForm({ ...form, passport_photo: b64 });
      // Auto-save photo immediately
      await axios.put(`${API_URL}/api/sis/students/${studentId}`, { passport_photo: b64 });
      toast.success('Photo updated');
      fetchProfile();
    } catch (err) { toast.error('Photo upload failed'); }
  };

  const downloadTranscript = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/sis/students/${studentId}/transcript`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `transcript_${profile.student.registration_number || studentId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Download failed'); }
  };

  if (loading) return <div className="text-center py-12 text-muted-foreground">Loading…</div>;
  if (!profile) return <div className="text-center py-12 text-muted-foreground">Student not found</div>;

  const s = profile.student;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/sis/students')} data-testid="back-to-list">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <Button variant="outline" size="sm" onClick={downloadTranscript} data-testid="download-transcript">
          <Download className="h-4 w-4 mr-1" /> Transcript PDF
        </Button>
      </div>

      {/* Header card */}
      <Card>
        <CardContent className="pt-6 flex flex-col md:flex-row items-start md:items-center gap-4">
          <div className="relative">
            <Avatar className="h-24 w-24 border-2 border-primary/20">
              {s.passport_photo ? (
                <img src={s.passport_photo} alt={s.name} className="h-full w-full object-cover rounded-full" />
              ) : (
                <AvatarFallback className="text-2xl bg-primary/10 text-primary">{(s.name || '?').slice(0, 1)}</AvatarFallback>
              )}
            </Avatar>
            <label className="absolute -bottom-1 -right-1 p-1.5 bg-primary text-white rounded-full cursor-pointer hover:bg-primary/90" data-testid="photo-upload-btn">
              <Camera className="h-3.5 w-3.5" />
              <input type="file" accept="image/*" className="hidden" onChange={onPhotoChange} data-testid="photo-upload-input" />
            </label>
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-heading font-bold">{s.name}</h1>
            <div className="text-sm text-muted-foreground space-x-3">
              <span>{s.registration_number || 'No Reg #'}</span>
              <span>· {(s.school || '').replace('_', ' ')}</span>
              <span>· {s.student_class || '—'}</span>
            </div>
            <div className="flex gap-1.5 mt-2 flex-wrap">
              {s.sponsorship_enrolled && <Badge variant="outline" className="bg-pink-50 text-pink-700 border-pink-200">Sponsored</Badge>}
              {s.catchup_enrolled && <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">Catch-up</Badge>}
              {s.scholarship_status && <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">{s.scholarship_status}</Badge>}
              {(s.is_active === false) && <Badge variant="outline" className="bg-gray-100 text-gray-700">Inactive</Badge>}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <div className="text-2xl font-bold text-primary">{profile.attendance.percentage != null ? `${profile.attendance.percentage}%` : '—'}</div>
              <div className="text-xs text-muted-foreground">Attendance</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-primary">{profile.stats.gpa != null ? `${profile.stats.gpa}%` : '—'}</div>
              <div className="text-xs text-muted-foreground">Avg Grade</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-primary">{profile.stats.engagement_count}</div>
              <div className="text-xs text-muted-foreground">Activities</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="personal">
        <TabsList className="grid grid-cols-5 w-full md:w-auto">
          <TabsTrigger value="personal" data-testid="tab-personal"><User className="h-3.5 w-3.5 mr-1" />Personal</TabsTrigger>
          <TabsTrigger value="health" data-testid="tab-health"><Heart className="h-3.5 w-3.5 mr-1" />Health</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="tab-attendance"><CalendarCheck className="h-3.5 w-3.5 mr-1" />Attendance</TabsTrigger>
          <TabsTrigger value="grades" data-testid="tab-grades"><GraduationCap className="h-3.5 w-3.5 mr-1" />Grades</TabsTrigger>
          <TabsTrigger value="programs" data-testid="tab-programs"><Award className="h-3.5 w-3.5 mr-1" />Programs</TabsTrigger>
        </TabsList>

        {/* Personal */}
        <TabsContent value="personal" className="mt-4">
          <PersonalTab s={s} form={form} setForm={setForm} editing={editing} setEditing={setEditing} save={save} />
        </TabsContent>

        {/* Health */}
        <TabsContent value="health" className="mt-4">
          <HealthTab s={s} form={form} setForm={setForm} editing={editing} setEditing={setEditing} save={save} />
        </TabsContent>

        {/* Attendance */}
        <TabsContent value="attendance" className="mt-4">
          <AttendanceTab profile={profile} />
        </TabsContent>

        {/* Grades */}
        <TabsContent value="grades" className="mt-4">
          <GradesTab studentId={studentId} grades={profile.grades} onChange={fetchProfile} />
        </TabsContent>

        {/* Programs */}
        <TabsContent value="programs" className="mt-4">
          <ProgramsTab s={s} form={form} setForm={setForm} editing={editing} setEditing={setEditing} save={save}
            engagement={profile.engagement} studentId={studentId} onEngagementChange={fetchProfile} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// ============ Tabs ============

const EditableField = ({ label, name, form, setForm, editing, type = 'text', options }) => {
  const value = form[name] ?? '';
  if (!editing) {
    return (
      <div>
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-sm font-medium">{value || '—'}</div>
      </div>
    );
  }
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      {type === 'select' ? (
        <Select value={value} onValueChange={(v) => setForm({ ...form, [name]: v })}>
          <SelectTrigger data-testid={`edit-${name}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            {options.map((o) => <SelectItem key={o.value || o} value={o.value || o}>{o.label || o}</SelectItem>)}
          </SelectContent>
        </Select>
      ) : type === 'textarea' ? (
        <Textarea value={value} onChange={(e) => setForm({ ...form, [name]: e.target.value })} data-testid={`edit-${name}`} />
      ) : (
        <Input type={type} value={value} onChange={(e) => setForm({ ...form, [name]: e.target.value })} data-testid={`edit-${name}`} />
      )}
    </div>
  );
};

const TabHeader = ({ title, editing, setEditing, save }) => (
  <div className="flex items-center justify-between mb-4">
    <h3 className="font-semibold">{title}</h3>
    {editing ? (
      <div className="flex gap-2">
        <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
        <Button size="sm" onClick={save} data-testid="save-changes-btn"><Save className="h-4 w-4 mr-1" />Save</Button>
      </div>
    ) : (
      <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="edit-section-btn">Edit</Button>
    )}
  </div>
);

const PersonalTab = ({ s, form, setForm, editing, setEditing, save }) => (
  <Card>
    <CardContent className="pt-6">
      <TabHeader title="Personal Information" editing={editing} setEditing={setEditing} save={save} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <EditableField label="Full Name" name="name" form={form} setForm={setForm} editing={editing} />
        <EditableField label="Registration #" name="registration_number" form={form} setForm={setForm} editing={editing} />
        <EditableField label="Date of Birth" name="date_of_birth" form={form} setForm={setForm} editing={editing} type="date" />
        <EditableField label="Gender" name="gender" form={form} setForm={setForm} editing={editing} type="select"
          options={[{ value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }]} />
        <EditableField label="Nationality" name="nationality" form={form} setForm={setForm} editing={editing} />
        <EditableField label="Religion" name="religion" form={form} setForm={setForm} editing={editing} />
        <EditableField label="Tribe / Language" name="tribe_language" form={form} setForm={setForm} editing={editing} />
        <EditableField label="Home District" name="home_district" form={form} setForm={setForm} editing={editing} />
        <EditableField label="School" name="school" form={form} setForm={setForm} editing={editing} type="select"
          options={[{ value: 'suubi_primary', label: 'Suubi Primary' }, { value: 'suubi_secondary', label: 'Suubi Secondary' }]} />
        <EditableField label="Class" name="student_class" form={form} setForm={setForm} editing={editing} />
        <div className="md:col-span-3">
          <EditableField label="Home Address" name="home_address" form={form} setForm={setForm} editing={editing} type="textarea" />
        </div>
      </div>

      <h4 className="font-semibold mt-6 mb-3">Parent / Guardian</h4>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <EditableField label="Guardian First Name" name="guardian_first_name" form={form} setForm={setForm} editing={editing} />
        <EditableField label="Guardian Last Name" name="guardian_last_name" form={form} setForm={setForm} editing={editing} />
        <EditableField label="Relationship" name="guardian_relationship" form={form} setForm={setForm} editing={editing} />
        <EditableField label="Guardian Phone" name="guardian_phone" form={form} setForm={setForm} editing={editing} />
        <EditableField label="Guardian Email" name="guardian_email" form={form} setForm={setForm} editing={editing} />
        <EditableField label="Parent (legacy)" name="parent_names" form={form} setForm={setForm} editing={editing} />
      </div>

      <h4 className="font-semibold mt-6 mb-3">Emergency Contact</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <EditableField label="Contact Name" name="emergency_contact_name" form={form} setForm={setForm} editing={editing} />
        <EditableField label="Contact Phone" name="emergency_contact_phone" form={form} setForm={setForm} editing={editing} />
      </div>
    </CardContent>
  </Card>
);

const HealthTab = ({ s, form, setForm, editing, setEditing, save }) => (
  <Card>
    <CardContent className="pt-6">
      <TabHeader title="Health Information" editing={editing} setEditing={setEditing} save={save} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <EditableField label="Medical Conditions" name="medical_conditions" form={form} setForm={setForm} editing={editing} type="textarea" />
        <EditableField label="Allergies" name="allergies" form={form} setForm={setForm} editing={editing} type="textarea" />
        <EditableField label="Disability Status" name="disability_status" form={form} setForm={setForm} editing={editing} type="textarea" />
        <EditableField label="Medications" name="medications" form={form} setForm={setForm} editing={editing} type="textarea" />
        <div className="md:col-span-2">
          <EditableField label="Emergency Medical Instructions" name="emergency_medical_instructions" form={form} setForm={setForm} editing={editing} type="textarea" />
        </div>
        <div className="md:col-span-2">
          <EditableField label="Health Notes" name="health_notes" form={form} setForm={setForm} editing={editing} type="textarea" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const AttendanceTab = ({ profile }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
      <Card><CardContent className="pt-6 text-center">
        <div className="text-3xl font-bold text-primary">{profile.attendance.percentage != null ? `${profile.attendance.percentage}%` : '—'}</div>
        <div className="text-xs text-muted-foreground">Attendance</div>
      </CardContent></Card>
      <Card><CardContent className="pt-6 text-center">
        <div className="text-3xl font-bold text-green-700">{profile.attendance.present}</div>
        <div className="text-xs text-muted-foreground">Present days</div>
      </CardContent></Card>
      <Card><CardContent className="pt-6 text-center">
        <div className="text-3xl font-bold text-red-700">{profile.attendance.absent}</div>
        <div className="text-xs text-muted-foreground">Absent days</div>
      </CardContent></Card>
      <Card><CardContent className="pt-6 text-center">
        <div className="text-3xl font-bold text-amber-700">{profile.attendance.late}</div>
        <div className="text-xs text-muted-foreground">Late days</div>
      </CardContent></Card>
    </div>
    {profile.attendance.percentage != null && profile.attendance.percentage < 75 && (
      <Card className="bg-red-50 border-red-200"><CardContent className="pt-6 text-sm text-red-700">
        ⚠ Chronic absenteeism — attendance below 75% threshold. Please review.
      </CardContent></Card>
    )}
    <Card><CardContent className="pt-6 text-sm text-muted-foreground">
      Detailed daily attendance is recorded via <a href="/personnel/students" className="underline text-primary">Personnel → Students → Weekly Attendance</a>.
    </CardContent></Card>
  </div>
);

const GradesTab = ({ studentId, grades, onChange }) => {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ subject: '', term: '', year: new Date().getFullYear(), score: '', max_score: 100, teacher: '' });

  const submit = async () => {
    if (!form.subject || !form.term || form.score === '') { toast.error('Fill subject, term, and score'); return; }
    try {
      await axios.post(`${API_URL}/api/sis/grades`, { ...form, student_id: studentId });
      toast.success('Grade added');
      setAdding(false);
      setForm({ subject: '', term: '', year: new Date().getFullYear(), score: '', max_score: 100, teacher: '' });
      onChange();
    } catch (e) { toast.error('Add failed'); }
  };

  const deleteGrade = async (id) => {
    if (!window.confirm('Delete this grade?')) return;
    try { await axios.delete(`${API_URL}/api/sis/grades/${id}`); toast.success('Deleted'); onChange(); }
    catch (e) { toast.error('Delete failed'); }
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold">Academic Grades</h3>
          <Button size="sm" onClick={() => setAdding(true)} data-testid="add-grade-btn"><Plus className="h-4 w-4 mr-1" />Add Grade</Button>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Term</TableHead><TableHead>Subject</TableHead>
              <TableHead className="text-right">Score</TableHead>
              <TableHead className="text-right">%</TableHead>
              <TableHead>Grade</TableHead>
              <TableHead>Teacher</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {grades.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">No grades yet</TableCell></TableRow>
            ) : grades.map((g) => {
              const pct = ((g.score || 0) / (g.max_score || 100) * 100).toFixed(1);
              return (
                <TableRow key={g.id}>
                  <TableCell>{g.term}</TableCell>
                  <TableCell>{g.subject}</TableCell>
                  <TableCell className="text-right tabular-nums">{g.score} / {g.max_score || 100}</TableCell>
                  <TableCell className="text-right tabular-nums">{pct}%</TableCell>
                  <TableCell><Badge variant="outline">{g.grade_letter}</Badge></TableCell>
                  <TableCell className="text-sm">{g.teacher || '—'}</TableCell>
                  <TableCell><Button size="sm" variant="ghost" onClick={() => deleteGrade(g.id)} data-testid={`delete-grade-${g.id}`}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button></TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <Dialog open={adding} onOpenChange={setAdding}>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Grade</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Subject</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="grade-subject" /></div>
              <div><Label>Term</Label><Input value={form.term} onChange={(e) => setForm({ ...form, term: e.target.value })} placeholder="e.g. Term 1 2026" data-testid="grade-term" /></div>
              <div><Label>Year</Label><Input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: parseInt(e.target.value) })} data-testid="grade-year" /></div>
              <div><Label>Teacher</Label><Input value={form.teacher} onChange={(e) => setForm({ ...form, teacher: e.target.value })} data-testid="grade-teacher" /></div>
              <div><Label>Score</Label><Input type="number" value={form.score} onChange={(e) => setForm({ ...form, score: parseFloat(e.target.value) })} data-testid="grade-score" /></div>
              <div><Label>Max Score</Label><Input type="number" value={form.max_score} onChange={(e) => setForm({ ...form, max_score: parseFloat(e.target.value) })} data-testid="grade-max" /></div>
            </div>
            <DialogFooter><Button onClick={submit} data-testid="grade-submit">Add</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

const ProgramsTab = ({ s, form, setForm, editing, setEditing, save, engagement, studentId, onEngagementChange }) => {
  const [adding, setAdding] = useState(false);
  const [eform, setEform] = useState({ type: 'club', name: '', role: '', date_start: '', notes: '' });

  const submitEng = async () => {
    if (!eform.name) { toast.error('Name required'); return; }
    try {
      await axios.post(`${API_URL}/api/sis/engagement`, { ...eform, student_id: studentId });
      toast.success('Added');
      setAdding(false);
      setEform({ type: 'club', name: '', role: '', date_start: '', notes: '' });
      onEngagementChange();
    } catch (e) { toast.error('Add failed'); }
  };

  const deleteEng = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try { await axios.delete(`${API_URL}/api/sis/engagement/${id}`); toast.success('Deleted'); onEngagementChange(); }
    catch (e) { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-6">
          <TabHeader title="Program Enrollment" editing={editing} setEditing={setEditing} save={save} />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <ToggleField label="Sponsorship" name="sponsorship_enrolled" form={form} setForm={setForm} editing={editing} />
            <ToggleField label="Catch-up Learning" name="catchup_enrolled" form={form} setForm={setForm} editing={editing} />
            <ToggleField label="Feeding Program" name="feeding_program" form={form} setForm={setForm} editing={editing} />
            <ToggleField label="Counseling Support" name="counseling_support" form={form} setForm={setForm} editing={editing} />
            <ToggleField label="Active Status" name="is_active" form={form} setForm={setForm} editing={editing} defaultTrue />
            <EditableField label="Scholarship" name="scholarship_status" form={form} setForm={setForm} editing={editing} />
            <EditableField label="Academic Status" name="academic_status" form={form} setForm={setForm} editing={editing} type="select"
              options={[{ value: 'excellent', label: 'Excellent' }, { value: 'good_standing', label: 'Good Standing' }, { value: 'at_risk', label: 'At Risk' }]} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">Engagement (Clubs, Sports, Leadership)</h3>
            <Button size="sm" onClick={() => setAdding(true)} data-testid="add-engagement-btn"><Plus className="h-4 w-4 mr-1" />Add</Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow><TableHead>Type</TableHead><TableHead>Name</TableHead><TableHead>Role</TableHead><TableHead>Start</TableHead><TableHead>Notes</TableHead><TableHead></TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {engagement.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-4">None recorded</TableCell></TableRow>
              ) : engagement.map((e) => (
                <TableRow key={e.id}>
                  <TableCell><Badge variant="outline" className="capitalize">{e.type}</Badge></TableCell>
                  <TableCell>{e.name}</TableCell>
                  <TableCell>{e.role || '—'}</TableCell>
                  <TableCell className="text-xs">{e.date_start || '—'}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{e.notes || '—'}</TableCell>
                  <TableCell><Button size="sm" variant="ghost" onClick={() => deleteEng(e.id)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Dialog open={adding} onOpenChange={setAdding}>
            <DialogContent>
              <DialogHeader><DialogTitle>Add Engagement</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Type</Label>
                  <Select value={eform.type} onValueChange={(v) => setEform({ ...eform, type: v })}>
                    <SelectTrigger data-testid="eng-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="club">Club</SelectItem>
                      <SelectItem value="sport">Sport</SelectItem>
                      <SelectItem value="leadership">Leadership</SelectItem>
                      <SelectItem value="extracurricular">Extracurricular</SelectItem>
                      <SelectItem value="behavior_note">Behavior Note</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Name</Label><Input value={eform.name} onChange={(e) => setEform({ ...eform, name: e.target.value })} data-testid="eng-name" /></div>
                <div><Label>Role</Label><Input value={eform.role} onChange={(e) => setEform({ ...eform, role: e.target.value })} data-testid="eng-role" /></div>
                <div><Label>Start Date</Label><Input type="date" value={eform.date_start} onChange={(e) => setEform({ ...eform, date_start: e.target.value })} data-testid="eng-date" /></div>
                <div className="col-span-2"><Label>Notes</Label><Textarea value={eform.notes} onChange={(e) => setEform({ ...eform, notes: e.target.value })} data-testid="eng-notes" /></div>
              </div>
              <DialogFooter><Button onClick={submitEng} data-testid="eng-submit">Add</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>
    </div>
  );
};

const ToggleField = ({ label, name, form, setForm, editing, defaultTrue = false }) => {
  const value = form[name] != null ? !!form[name] : defaultTrue;
  if (!editing) {
    return (
      <div>
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-sm font-medium">{value ? '✓ Yes' : '— No'}</div>
      </div>
    );
  }
  return (
    <label className="flex items-center gap-2">
      <Checkbox checked={value} onCheckedChange={(v) => setForm({ ...form, [name]: !!v })} data-testid={`toggle-${name}`} />
      <span className="text-sm">{label}</span>
    </label>
  );
};

export default SISStudentProfilePage;
